import { useCallback } from "react";
import {
  computeMaxBumpCandidates,
  maxBumpOfferOf,
  type CurrentMax,
} from "@heraion/shared";

import type { MaxBumpInput } from "../Logger/LoggerScreen";
import type { DurableWriteFn } from "../seams";

const MAX_BUMPS_PATH = "/api/me/max-bumps";

export type UseMaxBumpOfferDeps = {
  maxes: ReadonlyMap<string, number>;
  maxMeta: ReadonlyMap<string, CurrentMax>;
  /** False until the first MAX read lands: then nothing is offered. */
  loaded: boolean;
  /** The host's write. It also refreshes the bell once an offer lands:
   *  mobile through the request queue's settle listener (first send and
   *  replay alike, bell/maxBumpReplay.ts), web in its own wrapper. */
  durableWrite: DurableWriteFn;
};

/**
 * After a save (#182): what the workout proposes for each MAX, raise or
 * lower, goes to the server, which keeps what still holds and turns it into a
 * bell row. Both hosts use it; only `durableWrite` differs (mobile queues
 * offline, web retries once back online). Returns the exercises offered, for
 * the post-session summary line, whatever the write's outcome.
 */
export function useMaxBumpOffer({
  maxes,
  maxMeta,
  loaded,
  durableWrite,
}: UseMaxBumpOfferDeps): (input?: MaxBumpInput) => readonly string[] {
  return useCallback(
    (input?: MaxBumpInput): readonly string[] => {
      // Before the first MAX read, offer nothing. Under Option B an empty map
      // raises no set, but a calibration (#353) would propose a first MAX
      // for an exercise whose MAX this client has not read.
      if (!input || !loaded) return [];
      const currentMaxes: Record<string, number> = {};
      for (const [slug, kg] of maxes) currentMaxes[slug] = kg;
      const currentMaxesDetailed: Record<string, CurrentMax> = {};
      for (const [slug, m] of maxMeta) currentMaxesDetailed[slug] = m;
      const pending = computeMaxBumpCandidates({
        workout: input.workout,
        exercisePatterns: input.exercisePatterns,
        currentMaxes,
        // {valueKg, setAt}: enables the downward direction.
        currentMaxesDetailed,
        // Pre-save evidence: one light day cannot lower a MAX below a recent
        // stronger session, and MX-11's corroboration count.
        recentBestE1RM: input.recentBestE1RM,
        recentSessionCount: input.recentSessionCount,
        workoutId: input.workoutId,
      });
      if (!pending) return [];
      const offer = maxBumpOfferOf(pending, input.workout.startedAt);
      if (!offer) return [];
      void durableWrite({ method: "POST", path: MAX_BUMPS_PATH, body: offer })
        .then((result) => {
          // Background work: a failure is logged, never shown as an error.
          if (result.outcome === "rejected")
            console.warn("[maxBumps] offer not sent", result.error);
        })
        .catch((err: unknown) => {
          // Only a queue that could not persist throws here.
          console.warn("[maxBumps] offer lost", err);
        });
      return offer.candidates.map((c) => c.exerciseId);
    },
    [maxes, maxMeta, loaded, durableWrite],
  );
}
